import type from './SupportingDocument.type';
import { toasterMsg } from '../../Toaster/Toaster';
import * as SupportingDocumentService from '../../../services/supporting-document.service';
import { runBackgroundUpload } from '../../../services/background-upload';
import uploaderType from '../../../Pages/UploaderRedux/store/uploader.type';
import { getSlugName } from '../../helper';
import { logError } from '../../../utils/logger';
import { generateUniqueFilePrefix } from '../../../Pages/DevlopmentOrder/DesignDetail/utils/helpers';
import { isLiveInAnotherTab } from '../../../utils/upload-tab-coordinator';

const BOM_SUPPORTING_DOC_MODULE = 'bom-supporting-doc';

/**
 * Builds the S3 object key for a BOM supporting document, mirroring the legacy
 * multer key so the stored object lands in the same tenant path. Dev-order
 * uploads nest under the dev order; library uploads (no dev order) nest directly
 * under BOM.
 * @param {Object} context - { devOrderId, bomId, libId }.
 * @param {File} file - The file being uploaded.
 * @returns {string} The S3 object key.
 */
const buildBomSupportingDocKey = (context, file) => {
  const slug = getSlugName();
  const bomKeyId = context.bomId || context.libId || generateUniqueFilePrefix();
  const uniquePrefix = generateUniqueFilePrefix();
  const safeName = file.name.replace(/ +/g, '');
  if (context.devOrderId) {
    return `${slug}/development-order/${context.devOrderId}/BOM/${bomKeyId}/documents/${uniquePrefix}_${safeName}`;
  }
  return `${slug}/BOM/${bomKeyId}/documents/${uniquePrefix}_${safeName}`;
};

/**
 * Builds the from-s3 save payload shared by the initial upload and the resume
 * path, so a BOM document persisted after a resume is identical to one persisted
 * in a single pass.
 * @param {Object} context - BOM metadata (bomType, libId, devOrderId, docType, ...).
 * @param {File} file - The uploaded file.
 * @param {string} objectKey - The stored S3 key.
 * @returns {Object} The from-s3 save payload.
 */
const buildBomSavePayload = (context, file, objectKey) => ({
  s3Key: objectKey,
  fileName: context.fileName || file.name,
  fileSize: file.size,
  mimeType: file.type || '',
  bomType: context.bomType,
  libId: context.libId || null,
  devOrderId: context.devOrderId || null,
  docType: context.docType || null,
  colourWay: context.colourWay || null,
  placement: context.placement || null,
  reference: context.reference || null,
  documentType: 'document',
  isAddToAssetLibrary: context.isAddToAssetLibrary === true,
});

/**
 * Uploads a BOM supporting document in the background via the shared multipart
 * engine + floating widget. The popup can close immediately; progress shows on
 * the widget and the document is saved under the BOM on completion. Records a
 * pending-upload row so the upload is resumable after a logout/crash.
 * @param {Object} context - BOM upload context + metadata.
 * @param {File} file - The file to upload.
 * @param {Function} transactionMsg - i18n translator.
 * @param {Function} onComplete - Called after a successful save (refresh the table).
 */
export const uploadBomSupportingDocInBackground =
  (context, file, transactionMsg, onComplete) => async (dispatch) => {
    const persistMetadata = async (objectKey) => {
      const res = await SupportingDocumentService.saveBomSupportingDocFromS3(buildBomSavePayload(context, file, objectKey));
      if (res?.code === 200) {
        return { ok: true, message: `BOM_LIBRARY_VIEW.${res?.message}` };
      }
      return { ok: false, message: res?.data?.errMsg || res?.message || 'modules/design-detail:DOCUMENT_UPLOAD_FAILED' };
    };

    return dispatch(
      runBackgroundUpload(dispatch, {
        file,
        buildObjectKey: (uploadFile) => buildBomSupportingDocKey(context, uploadFile),
        persistMetadata,
        onSuccess: onComplete,
        pendingMeta: {
          module: BOM_SUPPORTING_DOC_MODULE,
          devOrderId: context.devOrderId || null,
          libId: context.libId || null,
          bomId: context.bomId || null,
          bomType: context.bomType || null,
          docType: context.docType || null,
          colourWay: context.colourWay || null,
          placement: context.placement || null,
          reference: context.reference || null,
          fileName: file.name,
          fileTitle: context.fileName || null,
          productNumber: context.productNumber || null,
          isAddToAssetLibrary: context.isAddToAssetLibrary === true,
        },
        onSuccessMessage: (message) => toasterMsg(transactionMsg(message), 'success'),
        onFailedMessage: (message) => toasterMsg(transactionMsg(message || 'modules/design-detail:DOCUMENT_UPLOAD_FAILED'), 'danger'),
        onCancelledMessage: () => toasterMsg(transactionMsg('modules/design-detail:DOCUMENT_UPLOAD_CANCELLED'), 'warning'),
      }),
    );
  };

/**
 * Pushes resumable BOM uploads into the floating widget as 'resumable' rows,
 * carrying the BOM metadata each row needs to resume + rebuild the save payload.
 * @param {Function} dispatch - Redux dispatch.
 * @param {Array} records - Resumable-upload records from the API.
 */
const pushBomResumableRows = (dispatch, records) => {
  records.forEach((record) => {
    dispatch({
      type: uploaderType.ADD_FILES_IN_QUEUE_UPLOADER,
      payload: {
        [record.taskId]: {
          taskId: record.taskId,
          fileName: record.fileName,
          fileSize: record.fileSize,
          devOrderId: record.devOrderId,
          productNumber: record.productNumber,
          module: record.module,
          libId: record.libId,
          bomId: record.bomId,
          bomType: record.bomType,
          docType: record.docType,
          colourWay: record.colourWay,
          placement: record.placement,
          reference: record.reference,
          fileTitle: record.fileTitle,
          isAddToAssetLibrary: record.isAddToAssetLibrary,
          percentage: 0,
          completed: false,
          cancellable: false,
          status: 'resumable',
        },
      },
    });
  });
};

/**
 * Fetches ALL of the logged-in user's resumable BOM uploads and pushes them into
 * the floating widget. Backs the global (any-page) resume prompt after relogin.
 */
export const fetchAllBomResumableUploads = () => async (dispatch) => {
  try {
    const res = await SupportingDocumentService.getAllBomResumableUploads();
    const resumable = res?.code === 200 && Array.isArray(res.data) ? res.data : [];
    const notLiveElsewhere = resumable.filter((record) => !isLiveInAnotherTab(record.taskId));
    pushBomResumableRows(dispatch, notLiveElsewhere);
    return notLiveElsewhere;
  } catch (err) {
    logError(err.toString());
    return [];
  }
};

/**
 * Resumes a previously-started BOM supporting-doc upload. Verifies the re-picked
 * file matches the pending record by name + size, fetches the resume state, and
 * continues the upload skipping already-stored parts. On mismatch the row is kept.
 * @param {Object} resumableRecord - The resumable row from the widget.
 * @param {File} repickedFile - The file the user re-picked.
 * @param {Function} transactionMsg - i18n translator.
 * @param {Function} onComplete - Called after a successful save.
 */
export const resumeBomSupportingUpload =
  (resumableRecord, repickedFile, transactionMsg, onComplete) => async (dispatch) => {
    if (resumableRecord.module !== BOM_SUPPORTING_DOC_MODULE) {
      return false;
    }
    if (repickedFile.name !== resumableRecord.fileName || repickedFile.size !== resumableRecord.fileSize) {
      toasterMsg(transactionMsg('modules/design-detail:RESUME_FILE_MISMATCH'), 'danger');
      return false;
    }

    /**
     * The widget row created at upload-start carries only progress fields, so a resume in the same
     * session lacks the BOM metadata (bomType, docType, …) the save requires. Re-fetch the
     * authoritative pending record so the resumed save is built from the persisted context rather
     * than the impoverished in-memory row.
     */
    const resumeRecord = await resolveBomResumeRecord(resumableRecord);
    if (!resumeRecord?.bomType) {
      toasterMsg(transactionMsg('modules/design-detail:RESUME_EXPIRED'), 'danger');
      dispatch(discardBomResumableUpload(resumableRecord.taskId));
      return false;
    }

    const stateRes = await SupportingDocumentService.getBomUploadResumeState(resumableRecord.taskId);
    if (stateRes?.code !== 200 || !stateRes.data?.uploadId) {
      toasterMsg(transactionMsg('modules/design-detail:RESUME_EXPIRED'), 'danger');
      dispatch({ type: uploaderType.REMOVE_TASK_FROM_LIST, payload: resumableRecord.taskId });
      return false;
    }
    const { uploadId, objectKey, partSize, partCount, uploadedPartNumbers } = stateRes.data;

    const context = {
      fileName: resumeRecord.fileTitle || resumeRecord.fileName,
      bomType: resumeRecord.bomType,
      libId: resumeRecord.libId,
      devOrderId: resumeRecord.devOrderId,
      bomId: resumeRecord.bomId,
      docType: resumeRecord.docType,
      colourWay: resumeRecord.colourWay,
      placement: resumeRecord.placement,
      reference: resumeRecord.reference,
      isAddToAssetLibrary: resumeRecord.isAddToAssetLibrary === true,
    };

    const persistMetadata = async (savedObjectKey) => {
      const res = await SupportingDocumentService.saveBomSupportingDocFromS3(buildBomSavePayload(context, repickedFile, savedObjectKey));
      if (res?.code === 200) {
        return { ok: true, message: `BOM_LIBRARY_VIEW.${res?.message}` };
      }
      return { ok: false, message: res?.data?.errMsg || res?.message || 'modules/design-detail:DOCUMENT_UPLOAD_FAILED' };
    };

    return dispatch(
      runBackgroundUpload(dispatch, {
        file: repickedFile,
        buildObjectKey: () => objectKey,
        persistMetadata,
        onSuccess: onComplete,
        completionScope: { module: BOM_SUPPORTING_DOC_MODULE, devOrderId: resumeRecord.devOrderId },
        resume: {
          taskId: resumableRecord.taskId,
          objectKey,
          state: { uploadId, partSize, partCount, uploadedPartNumbers },
        },
        onSuccessMessage: (message) => toasterMsg(transactionMsg(message), 'success'),
        onFailedMessage: (message) => toasterMsg(transactionMsg(message || 'modules/design-detail:DOCUMENT_UPLOAD_FAILED'), 'danger'),
        onCancelledMessage: () => toasterMsg(transactionMsg('modules/design-detail:DOCUMENT_UPLOAD_CANCELLED'), 'warning'),
      }),
    );
  };

/**
 * Discards a resumable BOM upload: aborts the S3 multipart upload, clears the
 * pending record, and removes the row from the floating widget.
 * @param {string} taskId - Client task id of the pending upload.
 */
export const discardBomResumableUpload = (taskId) => async (dispatch) => {
  try {
    await SupportingDocumentService.discardBomResumableUpload(taskId);
  } catch (err) {
    logError(err.toString());
  } finally {
    dispatch({ type: uploaderType.REMOVE_TASK_FROM_LIST, payload: taskId });
  }
};

export const saveDocuments = (data, transactionMsg) => async () => {
  try {
    const res = await SupportingDocumentService.uploadDocuments(data);
    if (res?.code === 200) {
      const msg = transactionMsg(`BOM_LIBRARY_VIEW.${res.message}`);
      toasterMsg(msg, 'success');
      return res;
    }
    if (res?.data?.errMsg) {
      toasterMsg(res.data.errMsg, 'danger');
    } else {
      toasterMsg('Exceded the allowed file size', 'danger');
    }
    return res;
  } catch (err) {
    toasterMsg(err.toString(), 'danger');
  }
};

export const AddColDocs = (data, transactionMsg) => async () => {
  try {
    const res = await SupportingDocumentService.addToColourLib(data);
    if (res?.code === 200) {
      const msg = transactionMsg(res.message);
      toasterMsg(msg, 'success');
    } else {
      toasterMsg(res.data.errMsg, 'danger');
    }
    return true;
  } catch (err) {
    toasterMsg(err.toString(), 'danger');
  }
};

export const getSupportingDocuments = (data) => async (dispatch) => {
  try {
    const res = await SupportingDocumentService.getSupportingDocuments(data);
    if (res?.code === 200 && res?.data) {
      return await dispatch({
        type: type.FETCH_SUPPORTING_DOCS,
        payload: res.data,
      });
    }
  } catch (err) {
    toasterMsg(err.toString(), 'danger');
  }
};

export const getMergedDocs = (data, t) => async (dispatch) => {
  try {
    const res = await SupportingDocumentService.getMergedDocuments(data);
    if (res?.code === 200 && res?.data !== '') {
      if (res.data === '') {
        toasterMsg(t('No Pdf(s) to merge'), 'danger');
      } else {
        await dispatch({
          type: type.FETCH_MERGED_DOCS,
          payload: res.data,
        });
      }
      return true;
    }
    toasterMsg(t(res?.data?.errMsg), 'danger');
  } catch (err) {
    toasterMsg(err.toString(), 'danger');
  }
};

export const deleteMergedDocs = (data) => async (dispatch) => {
  try {
    const res = await SupportingDocumentService.deleteMergedDocuments(data);
    if (res?.code === 200) {
      await dispatch({
        type: type.DELETE_MERGED_DOCS,
        payload: res?.data,
      });
      return true;
    }
    toasterMsg(res?.data?.errMsg, 'danger');
  } catch (err) {
    toasterMsg(err.toString(), 'danger');
  }
};

export const updateDocuments = (data, transactionMsg) => async () => {
  try {
    const res = await SupportingDocumentService.updateDocuments(data);
    if (res?.code === 200) {
      const msg = transactionMsg(`BOM_LIBRARY_VIEW.${res.message}`);
      toasterMsg(msg, 'success');
      return res;
    }
    toasterMsg(res?.data?.errMsg, 'danger');
  } catch (err) {
    toasterMsg(err.toString(), 'danger');
  }
};

export const unmountSupportingDocuments = () => async (dispatch) => {
  return dispatch({
    type: type.UNMOUNT_SUPPORTING_DOCS,
  });
};

export const deleteSupportingDocuments = (params, transactionMsg, val) => async (dispatch) => {
  try {
    const res = await SupportingDocumentService.deleteSupportingDocuments(params);
    if (res?.code === 200) {
      if (!val) {
        const msg = transactionMsg(`BOM_LIBRARY_VIEW.${res.message}`);
        toasterMsg(msg, 'success');
        return await dispatch({
          type: type.DELETE_SUPPORTING_DOCS,
          payload: params.ids,
        });
      }
      return res;
    }
    toasterMsg(res?.data?.errMsg, 'danger');
  } catch (err) {
    toasterMsg(err.toString(), 'danger');
  }
};

export const deleteAllUnSavedSupportingDocuments = (params) => async () => {
  try {
    const res = await SupportingDocumentService.deleteAllUnSavedSupportingDocuments(params);
    if (res?.code === 200) {
      return res;
    }
    toasterMsg(res?.data?.errMsg, 'danger');
  } catch (err) {
    toasterMsg(err.toString(), 'danger');
  }
};

/**
 * Returns the full BOM pending-upload record for a resume. The floating-widget row created at
 * upload-start carries only progress fields, so when it lacks the BOM metadata the save needs
 * (bomType in particular) the authoritative record is re-fetched from the API and matched by
 * taskId. A row that already has bomType is used as-is, avoiding a needless request.
 * @param {Object} resumableRecord - The widget row the user chose to resume.
 * @returns {Promise<Object|null>} The record with BOM metadata, or null when it cannot be resolved.
 */
const resolveBomResumeRecord = async (resumableRecord) => {
  if (resumableRecord?.bomType) {
    return resumableRecord;
  }
  try {
    const res = await SupportingDocumentService.getAllBomResumableUploads();
    const records = res?.code === 200 && Array.isArray(res.data) ? res.data : [];
    const match = records.find((record) => record.taskId === resumableRecord.taskId);
    return match || null;
  } catch (err) {
    logError(err.toString());
    return null;
  }
};
